import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import IconUp from '../../icons/up';
import IconDown from '../../icons/down';
import IconLoader from '../../icons/loader';
import ClickOutsideDetector from '../../autocomplete/click-outside-detector';
import formatСhatDate from './format-chat-date';
import { PermissionEnum } from '../../../base-types';
import { TransactionChatsTypes } from './types';

import styles from './chats.module.css';

export default function TransactionChats({
  chats,
  companyOfficials,
  individualCustomer,
  onCreateTransactionChat,
  chatCreateInProgress,
  currentEmployee,
  internal,
  recipients,
  senders,
  recipientAccount,
  senderAccount,
  activePermissions,
  transactionChatsIsLoading,
}: TransactionChatsTypes) {
  const [menuVisible, setMenuVisible] = useState(false);

  let content;

  if (!activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
    content = (
      <div className={styles.emptyChats}>
        You don&apos;t have access to this section
      </div>
    );
  } else if (transactionChatsIsLoading) {
    content = (
      <div className={styles.contentLoader}>
        <IconLoader />
      </div>
    );
  } else if (!transactionChatsIsLoading && !chats?.length) {
    content = <div className={styles.emptyChats}>There are no chats</div>;
  } else if (!transactionChatsIsLoading && chats) {
    content = (
      <div className={styles.chatsList}>
        {chats.map((item) => {
          const customerInChat = `${item?.user?.profile?.firstName} ${item?.user?.profile?.lastName}`;

          return (
            <NavLink
              to={`/messages/requests/in-progress/${item?.id}`}
              className={`${styles.chatItem} ${
                item.status === 'CLOSED' ? styles.chatCosed : ''
              }`}
              key={item.id}
              target="_blank"
            >
              <div className={styles.chatItemTop}>
                <div className={styles.chatItemTitle}>
                  <span>{item.title}</span>
                </div>
                <div className={styles.chatItemRight}>
                  {item.status === 'CLOSED' ? (
                    <div className={styles.closedStatus}>Chat Closed</div>
                  ) : (
                    formatСhatDate(
                      item?.messages?.edges[0]?.node?.createdAt ||
                        item.updatedAt ||
                        item.createdAt,
                      true,
                    )
                  )}
                </div>
              </div>
              {item?.messages?.edges?.length > 0 && (
                <div className={styles.chatItemText}>
                  {item?.messages?.edges[0]?.node.content ||
                    item?.messages?.edges[0]?.node?.document?.name}
                </div>
              )}
              <div className={styles.chatAssigned}>
                <span>{customerInChat}</span>

                {item?.employee && item?.employeeId === currentEmployee?.id
                  ? 'Assigned to you'
                  : `${item.employee?.profile?.firstName} ${item.employee?.profile?.lastName} (${item?.employee?.email})`}
              </div>
            </NavLink>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.chatsWrapper}>
      <div className={styles.chatsTop}>
        <div className={styles.chatsTitle}>Chats on this transaction</div>
        {activePermissions?.includes(PermissionEnum.CHANNELS_LIST) &&
          activePermissions?.includes(
            PermissionEnum.CHANNELS_CREATE_ACTION,
          ) && (
            <div className={styles.employeesWrapper}>
              <ClickOutsideDetector setOptionsListVisible={setMenuVisible}>
                <>
                  <button
                    type="button"
                    className={`${styles.selectEmployeesButton} ${
                      chatCreateInProgress
                        ? styles.selectEmployeesButtonDisabled
                        : ''
                    }`}
                    onClick={() => setMenuVisible(!menuVisible)}
                  >
                    {chatCreateInProgress ? 'Loading...' : 'Start new chat'}
                    {menuVisible ? (
                      <IconUp
                        className={`${styles.buttonIcon} ${styles.buttonIconUp}`}
                      />
                    ) : (
                      <IconDown className={styles.buttonIcon} />
                    )}
                  </button>
                  {menuVisible && (
                    <ul className={styles.employeesList}>
                      {!internal && (
                        <li className={styles.employeesListTitle}>
                          Select a customer to start the chat
                        </li>
                      )}
                      {internal && (
                        <>
                          <li className={styles.employeesListTitle}>
                            With Sender
                          </li>
                          {senders && senders.length > 0 && (
                            <>
                              <li>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuVisible(false);
                                    if (onCreateTransactionChat) {
                                      onCreateTransactionChat(
                                        senders?.map((item) => item.userId),
                                        'sender',
                                      );
                                    }
                                  }}
                                >
                                  Create chats with all Senders
                                </button>
                              </li>
                              {senders.map((item) => (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenuVisible(false);

                                      if (onCreateTransactionChat) {
                                        onCreateTransactionChat(
                                          [item.userId],
                                          'sender',
                                        );
                                      }
                                    }}
                                  >{`${item.user?.profile?.firstName} ${item.user?.profile?.lastName}`}</button>
                                </li>
                              ))}
                            </>
                          )}
                          {!senders?.length &&
                            senderAccount?.organization?.type ===
                              'INDIVIDUAL' && (
                              <li>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (onCreateTransactionChat) {
                                      onCreateTransactionChat([], 'sender');
                                    }
                                    setMenuVisible(false);
                                  }}
                                >
                                  {`${senderAccount?.organization?.firstName} ${senderAccount?.organization?.lastName}`}
                                </button>
                              </li>
                            )}
                          {!senders?.length &&
                            senderAccount?.organization?.type ===
                              'CORPORATE' && (
                              <li>There are no company officials</li>
                            )}
                          <li className={styles.employeesListTitle}>
                            With Recipient
                          </li>
                          {recipients && recipients.length > 0 && (
                            <>
                              <li>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuVisible(false);
                                    if (onCreateTransactionChat) {
                                      onCreateTransactionChat(
                                        recipients?.map((item) => item.userId),
                                        'recipient',
                                      );
                                    }
                                  }}
                                >
                                  Create chats with all Recipients
                                </button>
                              </li>
                              {recipients.map((item) => (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenuVisible(false);

                                      if (onCreateTransactionChat) {
                                        onCreateTransactionChat(
                                          [item.userId],
                                          'recipient',
                                        );
                                      }
                                    }}
                                  >{`${item.user?.profile?.firstName} ${item.user?.profile?.lastName}`}</button>
                                </li>
                              ))}
                            </>
                          )}
                          {!recipients?.length &&
                            recipientAccount?.organization?.type ===
                              'INDIVIDUAL' && (
                              <li>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (onCreateTransactionChat) {
                                      onCreateTransactionChat([], 'recipient');
                                    }
                                    setMenuVisible(false);
                                  }}
                                >
                                  {`${recipientAccount?.organization?.firstName} ${recipientAccount?.organization?.lastName}`}
                                </button>
                              </li>
                            )}
                          {!recipients?.length &&
                            recipientAccount?.organization?.type ===
                              'CORPORATE' && (
                              <li>There are no company officials</li>
                            )}
                        </>
                      )}
                      {companyOfficials &&
                        companyOfficials.length > 0 &&
                        !internal && (
                          <>
                            <li>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenuVisible(false);
                                  if (onCreateTransactionChat) {
                                    onCreateTransactionChat(
                                      companyOfficials?.map(
                                        (item) => item.userId,
                                      ),
                                    );
                                  }
                                }}
                              >
                                Create chats with all Customers
                              </button>
                            </li>
                            {companyOfficials.map((item) => (
                              <li key={item.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMenuVisible(false);
                                    if (onCreateTransactionChat) {
                                      onCreateTransactionChat([item.userId]);
                                    }
                                  }}
                                >{`${item.user?.profile?.firstName} ${item.user?.profile?.lastName}`}</button>
                              </li>
                            ))}
                          </>
                        )}
                      {!companyOfficials?.length &&
                        individualCustomer?.organization?.type ===
                          'INDIVIDUAL' &&
                        !internal && (
                          <li>
                            <button
                              type="button"
                              onClick={() => {
                                if (onCreateTransactionChat) {
                                  onCreateTransactionChat();
                                }
                                setMenuVisible(false);
                              }}
                            >
                              {`${individualCustomer?.organization?.firstName} ${individualCustomer?.organization?.lastName}`}
                            </button>
                          </li>
                        )}
                      {!companyOfficials?.length &&
                        individualCustomer?.organization?.type ===
                          'CORPORATE' &&
                        !internal && <li>There are no company officials</li>}
                    </ul>
                  )}
                </>
              </ClickOutsideDetector>
            </div>
          )}
      </div>
      {content}
    </div>
  );
}
